import createCommonHistory from '@hickory/root';
import { createEventCoordinator, domExists, ensureEncodedPathname, getStateFromHistory, ignorablePopstateEvent } from '@hickory/dom-utils';

function noop() { }
function Browser(options) {
    if (options === void 0) { options = {}; }
    if (!domExists()) {
        return;
    }
    if (!options.raw) {
        options.raw = ensureEncodedPathname;
    }
    var _a = createCommonHistory(options), createLocation = _a.createLocation, createPath = _a.createPath, confirmNavigation = _a.confirmNavigation, confirmWith = _a.confirmWith, removeConfirmation = _a.removeConfirmation, keygen = _a.keygen;
    var beforeDestroy = [];
    // when true, pop will run without attempting to get user confirmation
    var reverting = false;
    function locationFromBrowser(providedState) {
        var _a = window.location, pathname = _a.pathname, search = _a.search, hash = _a.hash;
        var path = pathname + search + hash;
        var _b = providedState || getStateFromHistory(), key = _b.key, state = _b.state;
        if (!key) {
            key = keygen.major();
            window.history.replaceState({ key: key, state: state }, "", path);
        }
        return createLocation(path, key, state);
    }
    function toHref(location) {
        return createPath(location);
    }
    var responseHandler;
    function finalizePush(location) {
        return function () {
            var path = toHref(location);
            var key = location.key, state = location.state;
            window.history.pushState({ key: key, state: state }, "", path);
            browserHistory.location = location;
            browserHistory.action = "PUSH";
        };
    }
    function finalizeReplace(location) {
        return function () {
            var path = toHref(location);
            var key = location.key, state = location.state;
            window.history.replaceState({ key: key, state: state }, "", path);
            browserHistory.location = location;
            browserHistory.action = "REPLACE";
        };
    }
    var browserHistory = {
        // set action before location because locationFromBrowser enforces that the location has a key
        action: (getStateFromHistory().key !== undefined
            ? "POP"
            : "PUSH"),
        location: locationFromBrowser(),
        // set response handler
        respondWith: function (fn) {
            responseHandler = fn;
            responseHandler({
                location: browserHistory.location,
                action: browserHistory.action,
                finish: noop,
                cancel: noop
            });
        },
        // convenience
        toHref: toHref,
        confirmWith: confirmWith,
        removeConfirmation: removeConfirmation,
        destroy: function destroy() {
            beforeDestroy.forEach(function (fn) {
                fn();
            });
        },
        // navigation
        navigate: function navigate(to) {
            var location = createLocation(to, null);
            var path = createPath(location);
            var currentPath = createPath(browserHistory.location);
            if (path === currentPath) {
                browserHistory.replace(to);
            }
            else {
                browserHistory.push(to);
            }
        },
        push: function push(to) {
            // the major version should be the current key + 1
            var key = keygen.major(browserHistory.location.key);
            var location = createLocation(to, key);
            confirmNavigation({
                to: location,
                from: browserHistory.location,
                action: "PUSH"
            }, function () {
                if (!responseHandler) {
                    return;
                }
                responseHandler({
                    location: location,
                    action: "PUSH",
                    finish: finalizePush(location),
                    cancel: noop
                });
            });
        },
        replace: function replace(to) {
            // pass the current key to just increment the minor portion
            var key = keygen.minor(browserHistory.location.key);
            var location = createLocation(to, key);
            confirmNavigation({
                to: location,
                from: browserHistory.location,
                action: "REPLACE"
            }, function () {
                if (!responseHandler) {
                    return;
                }
                responseHandler({
                    location: location,
                    action: "REPLACE",
                    finish: finalizeReplace(location),
                    cancel: noop
                });
            });
        },
        go: function go(num) {
            // Calling window.history.go with no value reloads the page. Instead
            // we will just call the responseHandler with the current location
            if (!num) {
                if (!responseHandler) {
                    return;
                }
                responseHandler({
                    location: browserHistory.location,
                    action: "POP",
                    finish: function () {
                        browserHistory.action = "POP";
                    },
                    cancel: noop
                });
            }
            else {
                window.history.go(num);
            }
        }
    };
    function pop(state) {
        // when we are reverting a pop (the user did not confirm navigation), we
        // just need to reset the boolean and return. The browser has already taken
        // care of updating the address bar and we never touched our internal values.
        if (reverting) {
            reverting = false;
            return;
        }
        var location = locationFromBrowser(state);
        var currentKey = browserHistory.location.key;
        var diff = keygen.diff(currentKey, location.key);
        confirmNavigation({
            to: location,
            from: browserHistory.location,
            action: "POP"
        }, function () {
            if (!responseHandler) {
                return;
            }
            responseHandler({
                location: location,
                action: "POP",
                finish: function () {
                    browserHistory.location = location;
                    browserHistory.action = "POP";
                },
                cancel: function (nextAction) {
                    if (nextAction === "POP") {
                        return;
                    }
                    reverting = true;
                    window.history.go(-1 * diff);
                }
            });
        }, function () {
            reverting = true;
            window.history.go(-1 * diff);
        });
    }
    // need to listen for browser navigation events
    beforeDestroy.push(createEventCoordinator({
        popstate: function (event) {
            if (ignorablePopstateEvent(event)) {
                return;
            }
            pop(event.state);
        }
    }));
    return browserHistory;
}

export default Browser;



// WEBPACK FOOTER //
// ./node_modules/@hickory/browser/dist/hickory-browser.es.js